import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  Badge,
  ContentLabel,
  ContentValue,
  FlexContainer,
  Link,
  Owners,
  RichTextValue,
  User
} from 'components/common';
import { Box } from 'components/common/box';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';
import { AchievementIcon, AchievementMultipleCompletionsList } from '.';

import type { GetAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: GetAchievementsRes['achievements'][0];
}

const AchievementRenderMultipleCompletions = ({ achievement }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const { title } = useAchievementTypes({ type: achievement?.type });

  return !achievement ? null : (
    <div className="achievement wrapper">
      <Box padding={0}>
        <div className="inner">
          <div className="header">
            <div className="icon">
              <AchievementIcon size="large" type={achievement.type} />
            </div>

            <div className="type">{title}</div>

            <div className="header-content">
              <div className="title">{achievement.title}</div>

              {achievement.description ? (
                <div className="description">
                  <RichTextValue html={achievement.description} />
                </div>
              ) : null}
            </div>
          </div>

          <div className="info">
            <DisplayAttributeInProductPage
              customAttributes={achievement.customAttributes}
              position="at start"
              productType={achievement.type}
            />

            {achievement.categories?.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonCategories}
                spacing={20}
              >
                <ContentValue>
                  <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                    {achievement.categories.map((category) => (
                      <Badge key={category} size="medium" text={category} />
                    ))}
                  </FlexContainer>
                </ContentValue>
              </ContentLabel>
            ) : null}

            {achievement.competencies.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.competenciesGranted}
                style={{ whiteSpace: 'nowrap' }}
              >
                <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                  {achievement.competencies.map((competency) => (
                    <CompetenciesProductBadge
                      key={`${competency.id}${
                        competency.level ? `-${competency.level.id}` : ''
                      }`}
                      competency={competency}
                    />
                  ))}
                </FlexContainer>
              </ContentLabel>
            ) : null}

            {!serverConfig.productHideOwner && (
              <ContentLabel
                horizontal={true}
                label={messages.commonOwners}
                spacing={20}
                style={{ whiteSpace: 'nowrap' }}
              >
                {achievement.owners?.length ? (
                  <FlexContainer style={{ gap: 5 }}>
                    <User
                      showNameOrUsername={false}
                      size="small"
                      user={achievement.owners?.[0]}
                    />

                    <Owners owners={achievement.owners} />
                  </FlexContainer>
                ) : (
                  messages.commonNoOwners
                )}
              </ContentLabel>
            )}

            {achievement.externalUrl && (
              <ContentLabel
                horizontal={true}
                label={messages.achievementExternalUrl}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue>
                  <Link
                    external={true}
                    href={achievement.externalUrl}
                    openInNewTab={true}
                    text={achievement.externalUrl}
                  />
                </ContentValue>
              </ContentLabel>
            )}

            <DisplayAttributeInProductPage
              customAttributes={achievement.customAttributes}
              productType={achievement.type}
            />
          </div>
        </div>
      </Box>

      <div className="completions">
        <AchievementMultipleCompletionsList
          achievement={{
            ...achievement,
            numberOfItems: achievement.template?.length || 0
          }}
          completions={achievement.completions || []}
          refreshRoute={true}
        />
      </div>

      <style jsx>{`
        .achievement {
          margin: 0 auto;
          max-width: 720px;
        }
        .inner {
          padding: 15px;
        }
        .header {
          text-align: center;
        }
        .type {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-style: italic;
          font-weight: 600;
        }
        .header-content {
          margin-top: 25px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 2rem;
          font-weight: 700;
          margin: 20px 0;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          text-align: justify;
        }
        .info {
          margin-top: 30px;
        }
        .completions {
          margin-top: 30px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .inner {
            padding: 25px;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementRenderMultipleCompletions;
